/**
 * Version + Snapshot picker — used inside ReleaseNetworkDialog when
 * the user creates a new room.
 *
 * The user picks one Version (``vN``) and one Snapshot. The Snapshot
 * dropdown filters to snapshots tagged with the chosen Version's
 * ``parentVersion`` — by construction snapshots never cross Versions.
 *
 * The list also includes a synthetic "Cut new version (v{nextVersion})"
 * entry that maps to ``versionRef = "v{nextVersion}"``. The backend
 * collapses that to "kind=new" and runs the standard release-cutting
 * path (snapshot capture, NetworkRelease row, baseline checkpoint,
 * SFN publish on PROD). This is the only way to release a *first*
 * version on a network with no releases yet.
 *
 * Defaults:
 *   * Version → latest released ``vN`` if any, else "Cut new (v1)".
 *   * Snapshot → release-baseline of the chosen Version. Hidden when
 *     "Cut new" is selected (no snapshot exists yet) or there's only
 *     one candidate.
 *
 * Pure controlled component. ``onChange`` fires with
 * ``{ versionRef, snapshotRef }`` whenever either picker changes.
 */
import React, { useEffect, useMemo } from 'react';
import { displayVersionLabel, predictNextVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';

/** Build the Version dropdown option list. The synthetic "Cut new version"
 *  entry is conditionally included:
 *
 *    - First-ever release (no ``activeReleases``) → always include "Cut new
 *      (v1)" because there's nothing to pin to.
 *    - Topology changed since the latest release (``topologyChanged=true``)
 *      → include "Cut new (vN+1)" so the user can publish the new topology.
 *    - Topology unchanged (``topologyChanged=false``) → omit the synthetic
 *      entry entirely. Cutting a new SFN version on identical topology
 *      pollutes the audit trail; the user just creates a new room/alias on
 *      the latest existing version instead.
 */
function buildVersionOptions({ releases, nextVersion, topologyChanged, nextLabel }) {
  const list = [];
  const activeReleases = (Array.isArray(releases) ? releases : [])
    .filter((r) => (r?.releaseStatus || 'ACTIVE_RELEASE') === 'ACTIVE_RELEASE')
    .filter((r) => Number.isFinite(Number(r?.releaseVersion)))
    .sort((a, b) => Number(b.releaseVersion) - Number(a.releaseVersion));

  const nv = Number.isFinite(Number(nextVersion)) && Number(nextVersion) >= 1
    ? Number(nextVersion)
    : null;
  const hasPriorRelease = activeReleases.length > 0;
  const predictedLabel = nextLabel || predictNextVersionLabel(releases, true);

  // Existing display labels — used to dedupe a synthetic "Cut new" entry
  // whose predicted label collides with one already in the list. A
  // collision is meaningless: the user can pin to that version directly,
  // and offering "Cut new (1.10)" alongside an existing "1.10" is just
  // noise. Treat this as a no-op signal.
  const existingLabels = new Set(
    activeReleases.map((r) => displayVersionLabel(r, releases)).filter(Boolean),
  );
  const collides = existingLabels.has(predictedLabel);

  const showCutNew = nv
    && (!hasPriorRelease || topologyChanged !== false)
    && !collides;
  if (showCutNew) {
    list.push({
      value: `v${nv}`,
      label: `Cut new version (${predictedLabel})`,
      isNew: true,
    });
  }
  for (const r of activeReleases) {
    const v = Number(r.releaseVersion);
    // value stays the functional ``vN`` key (feeds versionRef); label is the
    // semantic MAJOR.MINOR (or 'Release N' fallback, never raw vN).
    const displayLabel = displayVersionLabel(r, releases) || `Release ${v}`;
    list.push({
      value: `v${v}`,
      label: displayLabel,
      releasedAt: r.releasedAt,
      isNew: false,
    });
  }
  return list;
}

/** Filter snapshots to a given parent Version + sort newest first. */
function snapshotsForVersion(snapshots, versionRef) {
  if (!Array.isArray(snapshots) || !versionRef) return [];
  const matching = snapshots.filter((s) => s?.parentVersion === versionRef);
  matching.sort((a, b) => {
    const aBase = a?.reason === 'release-baseline' ? 0 : 1;
    const bBase = b?.reason === 'release-baseline' ? 0 : 1;
    if (aBase !== bBase) return aBase - bBase;
    const ta = String(a?.capturedAt || '');
    const tb = String(b?.capturedAt || '');
    return tb.localeCompare(ta);
  });
  return matching;
}

function snapshotDomKey(s) {
  return `${s?.kind || 'X'}::${s?.capturedAt || ''}`;
}

function snapshotLabel(s) {
  if (!s) return '(unnamed)';
  if (s.label) return s.label;
  if (s.reason === 'release-baseline') {
    const v = s.parentVersion || '';
    return `${v} baseline`;
  }
  if (s.kind === 'BASELINE') return 'Golden baseline';
  if (s.kind === 'USER_SNAPSHOT') return 'User snapshot';
  return s.capturedAt || '(unnamed)';
}

/** Default to the newest released vN; if none exist, fall back to the
 *  synthetic "Cut new" entry. The cut-new option is pinned to the same
 *  ``v{nextVersion}`` value as a real released vN — they're disambiguated
 *  by ``isNew``, which only the picker UI cares about. */
function pickDefaultVersion(versionOptions) {
  const released = versionOptions.find((o) => !o.isNew);
  if (released) return released.value;
  const cutNew = versionOptions.find((o) => o.isNew);
  return cutNew ? cutNew.value : '';
}

export default function VersionSnapshotPicker({
  releases,
  snapshots,
  nextVersion,
  value,
  onChange,
  loading = false,
  error = null,
  topologyChanged = true,
  nextLabel,
}) {
  const versionOptions = useMemo(
    () => buildVersionOptions({ releases, nextVersion, topologyChanged, nextLabel }),
    [releases, nextVersion, topologyChanged, nextLabel],
  );

  const currentVersion = value?.versionRef || pickDefaultVersion(versionOptions);
  const currentVersionOption = versionOptions.find((o) => o.value === currentVersion);
  const isCutNewSelected = !!currentVersionOption?.isNew;

  const matchingSnapshots = useMemo(
    () => (isCutNewSelected ? [] : snapshotsForVersion(snapshots, currentVersion)),
    [snapshots, currentVersion, isCutNewSelected],
  );

  const currentSnapshotKey = value?.snapshotRef || (matchingSnapshots[0]
    ? snapshotDomKey(matchingSnapshots[0])
    : '');

  useEffect(() => {
    if (loading) return;
    if (versionOptions.length === 0) return;
    const fallbackVersion = pickDefaultVersion(versionOptions);
    const next = value?.versionRef && versionOptions.some((o) => o.value === value.versionRef)
      ? value.versionRef
      : fallbackVersion;
    const nextOpt = versionOptions.find((o) => o.value === next);
    const nextSnapshots = nextOpt?.isNew ? [] : snapshotsForVersion(snapshots, next);
    const fallbackSnapshot = nextSnapshots[0];
    const previouslyPickedStillValid = value?.snapshotRef
      && nextSnapshots.some((s) => snapshotDomKey(s) === value.snapshotRef);
    const nextSnapshotRef = previouslyPickedStillValid
      ? value.snapshotRef
      : (fallbackSnapshot ? snapshotDomKey(fallbackSnapshot) : '');
    if (next !== value?.versionRef || nextSnapshotRef !== value?.snapshotRef) {
      onChange?.({ versionRef: next, snapshotRef: nextSnapshotRef });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, versionOptions, snapshots]);

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        Could not load Versions / Snapshots: {error}
      </div>
    );
  }

  // Snapshot dropdown only shows when there's a real choice (2+ rows).
  // Suppressed entirely when "Cut new" is selected — the snapshot is
  // captured at release time, no pre-existing options.
  const showSnapshotPicker = !isCutNewSelected && matchingSnapshots.length > 1;

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className={`grid grid-cols-1 gap-3 ${showSnapshotPicker ? 'sm:grid-cols-2' : ''}`}>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Version</span>
          <select
            data-testid="vsp-version"
            value={currentVersion}
            disabled={loading || versionOptions.length === 0}
            onChange={(e) => onChange?.({ versionRef: e.target.value, snapshotRef: '' })}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {versionOptions.length === 0 ? (
              <option value="">{loading ? 'Loading…' : 'No Versions available'}</option>
            ) : (
              versionOptions.map((o) => (
                <option key={`${o.isNew ? 'new' : 'rel'}::${o.value}`} value={o.value}>
                  {o.label}
                </option>
              ))
            )}
          </select>
        </label>

        {showSnapshotPicker ? (
          <label className="block">
            <span className="block text-sm font-medium text-gray-700">Snapshot</span>
            <select
              data-testid="vsp-snapshot"
              value={currentSnapshotKey || ''}
              disabled={loading || matchingSnapshots.length === 0}
              onChange={(e) => onChange?.({
                versionRef: currentVersion,
                snapshotRef: e.target.value,
              })}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {matchingSnapshots.map((s) => {
                const key = snapshotDomKey(s);
                return (
                  <option key={key} value={key}>
                    {snapshotLabel(s)}
                  </option>
                );
              })}
            </select>
          </label>
        ) : null}
      </div>

      {isCutNewSelected ? (
        <p className="text-xs leading-relaxed text-gray-600" data-testid="vsp-cut-new-note">
          A new immutable Version will be cut from the current network state when you click Release.
        </p>
      ) : null}
    </div>
  );
}

export const __test__ = {
  buildVersionOptions,
  snapshotsForVersion,
  snapshotLabel,
  snapshotDomKey,
  pickDefaultVersion,
};
